import React from 'react';
import { Link } from 'react-router-dom';
import { Camera, Calendar, Folder, MessageCircle, Star, Image, ArrowRight } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';

const ClientDashboard = () => {
  const bookings = [
    {
      id: 1,
      photographer: 'Sarah Johnson',
      photographerImage: 'https://images.unsplash.com/photo-1494790108377-be9c29b29330?w=100&h=100&fit=crop',
      type: 'Wedding Photography',
      date: 'December 15, 2024',
      status: 'completed',
      assetCount: 487,
      thumbnail: 'https://images.unsplash.com/photo-1606800052052-a08af7148866?w=400&h=300&fit=crop',
    },
    {
      id: 2,
      photographer: 'Michael Chen',
      photographerImage: 'https://images.unsplash.com/photo-1507003211169-0a1dd7228f2d?w=100&h=100&fit=crop',
      type: 'Product Photography',
      date: 'January 20, 2025',
      status: 'upcoming',
      assetCount: 0,
      thumbnail: 'https://images.unsplash.com/photo-1504674900247-0877df9cc836?w=400&h=300&fit=crop',
    },
  ];

  const messages = [
    {
      id: 1,
      photographer: 'Sarah Johnson',
      image: 'https://images.unsplash.com/photo-1494790108377-be9c29b29330?w=100&h=100&fit=crop',
      message: 'Your photos are ready! Check your vault.',
      time: '2 hours ago',
      unread: true,
    },
    {
      id: 2,
      photographer: 'Michael Chen',
      image: 'https://images.unsplash.com/photo-1507003211169-0a1dd7228f2d?w=100&h=100&fit=crop',
      message: 'Looking forward to our shoot next month!',
      time: '1 day ago',
      unread: false,
    },
  ];

  return (
    <div className="min-h-screen bg-background">
      {/* Header */}
      <header className="border-b bg-background sticky top-0 z-50">
        <div className="px-6 md:px-12 lg:px-24 py-4">
          <div className="flex items-center justify-between">
            <Link to="/" className="flex items-center space-x-2" data-testid="logo-link">
              <Camera className="w-8 h-8" />
              <span className="font-playfair text-2xl font-bold">Pixnest</span>
            </Link>
            <div className="flex items-center space-x-4">
              <Link to="/photographers">
                <Button variant="ghost" size="sm" data-testid="find-photographers">Find Photographers</Button>
              </Link>
              <Link to="/vault">
                <Button variant="ghost" size="sm" data-testid="my-vault">My Vault</Button>
              </Link>
              <Button variant="ghost" size="icon" data-testid="profile-button">
                <img
                  src="https://images.unsplash.com/photo-1472099645785-5658abf4ff4e?w=100&h=100&fit=crop"
                  alt="Profile"
                  className="w-8 h-8 rounded-full object-cover"
                />
              </Button>
            </div>
          </div>
        </div>
      </header>

      <div className="px-6 md:px-12 lg:px-24 py-12">
        {/* Welcome Section */}
        <div className="mb-12">
          <h1 className="font-playfair text-4xl md:text-5xl font-bold mb-2" data-testid="dashboard-title">
            Welcome Back, John
          </h1>
          <p className="text-lg text-muted-foreground" data-testid="dashboard-subtitle">
            Manage your bookings and access your photography assets
          </p>
        </div>

        {/* Quick Stats */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-12" data-testid="quick-stats">
          <Card>
            <CardHeader>
              <CardTitle className="text-base font-medium text-muted-foreground">Total Bookings</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="font-playfair text-4xl font-bold" data-testid="stat-bookings">12</div>
            </CardContent>
          </Card>
          <Card>
            <CardHeader>
              <CardTitle className="text-base font-medium text-muted-foreground">Assets Received</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="font-playfair text-4xl font-bold" data-testid="stat-assets">2,487</div>
            </CardContent>
          </Card>
          <Card>
            <CardHeader>
              <CardTitle className="text-base font-medium text-muted-foreground">Unread Messages</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="font-playfair text-4xl font-bold" data-testid="stat-messages">3</div>
            </CardContent>
          </Card>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
          {/* Main Content */}
          <div className="lg:col-span-2 space-y-8">
            {/* My Bookings */}
            <section data-testid="bookings-section">
              <div className="flex items-center justify-between mb-6">
                <h2 className="font-playfair text-3xl font-semibold" data-testid="bookings-title">My Bookings</h2>
                <Link to="/photographers">
                  <Button variant="outline" size="sm" data-testid="book-new">Book New</Button>
                </Link>
              </div>

              <div className="space-y-4">
                {bookings.map((booking) => (
                  <Card key={booking.id} className="overflow-hidden hover:shadow-lg transition-shadow" data-testid={`booking-${booking.id}`}>
                    <div className="grid md:grid-cols-4 gap-0">
                      <div className="relative h-48 md:h-auto">
                        <img
                          src={booking.thumbnail}
                          alt={booking.type}
                          className="w-full h-full object-cover"
                        />
                        {booking.status === 'completed' && booking.assetCount > 0 && (
                          <div className="absolute top-2 left-2">
                            <Badge className="bg-green-600" data-testid={`status-${booking.id}`}>Completed</Badge>
                          </div>
                        )}
                        {booking.status === 'upcoming' && (
                          <div className="absolute top-2 left-2">
                            <Badge variant="secondary" data-testid={`status-${booking.id}`}>Upcoming</Badge>
                          </div>
                        )}
                      </div>

                      <CardContent className="md:col-span-3 p-6">
                        <div className="flex items-start justify-between mb-4">
                          <div className="flex items-center space-x-3">
                            <img
                              src={booking.photographerImage}
                              alt={booking.photographer}
                              className="w-12 h-12 rounded-full object-cover"
                            />
                            <div>
                              <h3 className="font-playfair text-xl font-semibold" data-testid={`booking-type-${booking.id}`}>
                                {booking.type}
                              </h3>
                              <div className="text-sm text-muted-foreground" data-testid={`photographer-${booking.id}`}>
                                {booking.photographer}
                              </div>
                            </div>
                          </div>
                        </div>

                        <div className="flex items-center text-sm text-muted-foreground mb-4">
                          <Calendar className="w-4 h-4 mr-2" />
                          <span data-testid={`booking-date-${booking.id}`}>{booking.date}</span>
                        </div>

                        <div className="flex items-center gap-2">
                          {booking.assetCount > 0 && (
                            <Link to={`/vault/${booking.id}`}>
                              <Button size="sm" data-testid={`view-assets-${booking.id}`}>
                                <Folder className="w-4 h-4 mr-2" />
                                View Assets ({booking.assetCount})
                              </Button>
                            </Link>
                          )}
                          <Button variant="outline" size="sm" data-testid={`message-${booking.id}`}>
                            <MessageCircle className="w-4 h-4 mr-2" />
                            Message
                          </Button>
                        </div>
                      </CardContent>
                    </div>
                  </Card>
                ))}
              </div>
            </section>
          </div>

          {/* Sidebar */}
          <div className="lg:col-span-1 space-y-6">
            {/* Messages */}
            <Card data-testid="messages-card">
              <CardHeader>
                <CardTitle data-testid="messages-title">Messages</CardTitle>
                <CardDescription>Recent conversations</CardDescription>
              </CardHeader>
              <CardContent className="p-0">
                <div className="divide-y">
                  {messages.map((msg) => (
                    <div key={msg.id} className="p-4 hover:bg-muted/30 cursor-pointer transition-colors" data-testid={`message-${msg.id}`}>
                      <div className="flex items-start space-x-3">
                        <img
                          src={msg.image}
                          alt={msg.photographer}
                          className="w-10 h-10 rounded-full object-cover"
                        />
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center justify-between mb-1">
                            <div className="font-medium text-sm truncate" data-testid={`message-sender-${msg.id}`}>
                              {msg.photographer}
                            </div>
                            {msg.unread && (
                              <div className="w-2 h-2 bg-primary rounded-full" data-testid={`unread-${msg.id}`} />
                            )}
                          </div>
                          <p className="text-sm text-muted-foreground line-clamp-2" data-testid={`message-text-${msg.id}`}>
                            {msg.message}
                          </p>
                          <div className="text-xs text-muted-foreground mt-1" data-testid={`message-time-${msg.id}`}>
                            {msg.time}
                          </div>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
                <div className="p-4 border-t">
                  <Button variant="ghost" size="sm" className="w-full" data-testid="view-all-messages">
                    View All Messages
                    <ArrowRight className="w-4 h-4 ml-2" />
                  </Button>
                </div>
              </CardContent>
            </Card>

            {/* Quick Actions */}
            <Card data-testid="quick-actions">
              <CardHeader>
                <CardTitle>Quick Actions</CardTitle>
              </CardHeader>
              <CardContent className="space-y-2">
                <Link to="/photographers">
                  <Button variant="outline" className="w-full justify-start" data-testid="action-book">
                    <Calendar className="w-4 h-4 mr-2" />
                    Book a Photographer
                  </Button>
                </Link>
                <Link to="/vault">
                  <Button variant="outline" className="w-full justify-start" data-testid="action-vault">
                    <Folder className="w-4 h-4 mr-2" />
                    Browse Vault
                  </Button>
                </Link>
              </CardContent>
            </Card>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ClientDashboard;